import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calculator, ShoppingBag, Sparkles, User } from "lucide-react";

import { computeKarmaNumerology } from "@/lib/karma/numerology";
import { getSupabaseClient, resilientFetch } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

import { getKarmicDebtNumbers } from "../../shared/numerology";

const GOLD = "#d4af37";
const CART_STORAGE_KEY = "numeroloji_cart_v1";

type CartItem = { packageKey: string; qty: number };

type ShopProduct = {
  id: string;
  name: string;
  description: string;
  priceTry: number;
  active: boolean;
};

type NumerolojiPackage = {
  id: string;
  key: string;
  name: string;
  description: string;
  basePriceTry: number;
  discountedPriceTry?: number;
  imageUrl?: string;
  active: boolean;
};

type Review = {
  id: string;
  packageKey: string;
  name: string;
  rating: number;
  message: string;
};

type CircleItem = {
  id: string;
  name: string;
  activity: string | null;
  description: string | null;
  membersCount: number;
  imageUrl: string | null;
};

type CatalogResponse = {
  ok: true;
  products: ShopProduct[];
  packages: NumerolojiPackage[];
};

function safeParseCart(raw: string | null): CartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((it) => {
        const obj = it as any;
        const packageKey = typeof obj?.packageKey === "string" ? obj.packageKey : null;
        const qty = typeof obj?.qty === "number" ? obj.qty : Number(obj?.qty);
        if (!packageKey) return null;
        if (!Number.isFinite(qty) || qty <= 0) return null;
        return { packageKey, qty: Math.min(99, Math.trunc(qty)) } satisfies CartItem;
      })
      .filter(Boolean) as CartItem[];
  } catch {
    return [];
  }
}

function cartCountFromStorage() {
  const items = safeParseCart(globalThis?.localStorage?.getItem(CART_STORAGE_KEY) ?? null);
  return items.reduce((sum, it) => sum + (Number.isFinite(it.qty) ? it.qty : 0), 0);
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function shortKarmicInfo(code: number) {
  if (code === 13) return "13: Emek, disiplin ve düzen dersini çalıştırır.";
  if (code === 14) return "14: Özgürlük-kontrol dengesini öğretir.";
  if (code === 16) return "16: Ego ve ilişkilerde içsel dönüşüm çağırır.";
  if (code === 19) return "19: Bağımsızlık-sorumluluk dengesini test eder.";
  return `${code}: Karmik ders aktif.`;
}

function getPackageEffectivePrice(pkg: NumerolojiPackage) {
  return typeof pkg.discountedPriceTry === "number" && pkg.discountedPriceTry > 0 ? pkg.discountedPriceTry : pkg.basePriceTry;
}

async function fetchCatalog(): Promise<CatalogResponse> {
  try {
    const res = await resilientFetch("/api/numeroloji/catalog");
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) {
      throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
    }

    return {
      ok: true,
      products: Array.isArray(json?.products) ? (json.products as ShopProduct[]) : [],
      packages: Array.isArray(json?.packages) ? (json.packages as NumerolojiPackage[]) : [],
    };
  } catch {
    return { ok: true, products: [], packages: [] };
  }
}

async function fetchReviews(): Promise<Review[]> {
  try {
    const res = await resilientFetch("/api/numeroloji/reviews");
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) return [];
    return Array.isArray(json?.reviews) ? (json.reviews as Review[]) : [];
  } catch {
    return [];
  }
}

async function fetchPublicCircles(): Promise<CircleItem[]> {
  try {
    const supabase = getSupabaseClient();
    const { data: circles, error } = await supabase
      .from("circles")
      .select("id, name, activity, description, created_at, rules")
      .order("created_at", { ascending: false })
      .limit(6);

    if (error || !circles?.length) return [];

    const ids = circles.map((c: any) => String(c.id)).filter(Boolean);
    const { data: members, error: mErr } = await supabase.from("circle_members").select("circle_id").in("circle_id", ids);
    if (mErr) return [];

    const counts = new Map<string, number>();
    for (const row of members ?? []) {
      const id = String((row as any).circle_id ?? "");
      if (!id) continue;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }

    return circles.map((c: any) => ({
      id: String(c.id),
      name: String(c.name ?? ""),
      activity: typeof c.activity === "string" ? c.activity : null,
      description: typeof c.description === "string" ? c.description : null,
      membersCount: counts.get(String(c.id)) ?? 0,
      imageUrl:
        typeof c?.rules?.image_url === "string" ? c.rules.image_url : typeof c?.rules?.imageUrl === "string" ? c.rules.imageUrl : null,
    }));
  } catch {
    return [];
  }
}

export default function NumerolojiLanding() {
  const [cartCount, setCartCount] = useState(() => cartCountFromStorage());

  const [karmicForm, setKarmicForm] = useState({ firstName: "", lastName: "" });
  const [karmicCodes, setKarmicCodes] = useState<number[] | null>(null);

  const [analysisForm, setAnalysisForm] = useState({ firstName: "", lastName: "", day: "", month: "", year: "" });
  const [analysisResult, setAnalysisResult] = useState<ReturnType<typeof computeKarmaNumerology> | null>(null);
  const [reviewStartIndex, setReviewStartIndex] = useState(0);

  useEffect(() => {
    const onStorage = () => setCartCount(cartCountFromStorage());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const catalogQuery = useQuery({
    queryKey: ["numeroloji-home-catalog"],
    queryFn: fetchCatalog,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const reviewsQuery = useQuery({
    queryKey: ["numeroloji-home-reviews"],
    queryFn: fetchReviews,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const circlesQuery = useQuery({
    queryKey: ["numeroloji-home-public-circles"],
    queryFn: fetchPublicCircles,
    staleTime: 60_000,
  });

  const products = useMemo(() => (catalogQuery.data?.products ?? []).filter((p) => p.active).slice(0, 4), [catalogQuery.data?.products]);
  const packages = useMemo(() => (catalogQuery.data?.packages ?? []).filter((p) => p.active).slice(0, 4), [catalogQuery.data?.packages]);
  const cartItems = useMemo(() => safeParseCart(globalThis?.localStorage?.getItem(CART_STORAGE_KEY) ?? null), [cartCount]);
  const reviews = reviewsQuery.data ?? [];

  const rotatingReviews = useMemo(() => {
    if (!reviews.length) return [];
    const visibleCount = Math.min(3, reviews.length);
    return Array.from({ length: visibleCount }, (_, index) => reviews[(reviewStartIndex + index) % reviews.length]);
  }, [reviews, reviewStartIndex]);

  useEffect(() => {
    if (reviews.length <= 1) return;
    const timer = window.setInterval(() => {
      setReviewStartIndex((prev) => (prev + 1) % reviews.length);
    }, 8_000);
    return () => window.clearInterval(timer);
  }, [reviews.length]);

  const handleKarmicDebt = () => {
    const first = karmicForm.firstName.trim();
    const last = karmicForm.lastName.trim();
    if (!first && !last) {
      globalThis.alert?.("Lütfen isim ve soyisim gir.");
      return;
    }
    setKarmicCodes(getKarmicDebtNumbers(first || "Kullanıcı", last));
  };

  const handleQuickAnalysis = () => {
    const first = analysisForm.firstName.trim();
    const last = analysisForm.lastName.trim();
    const day = Number(analysisForm.day);
    const month = Number(analysisForm.month);
    const year = Number(analysisForm.year);

    if (!first || !last || !Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
      globalThis.alert?.("Lütfen ad, soyad ve doğum tarihini eksiksiz gir.");
      return;
    }

    const result = computeKarmaNumerology(`${first} ${last}`, { day, month, year });
    setAnalysisResult(result);
  };

  const addPackageToCart = (packageKey: string) => {
    const key = String(packageKey ?? "").trim();
    if (!key) return;

    const current = safeParseCart(globalThis?.localStorage?.getItem(CART_STORAGE_KEY) ?? null);
    const index = current.findIndex((entry) => String(entry.packageKey) === key);

    if (index >= 0) {
      const next = [...current];
      next[index] = { ...next[index], qty: Math.min(99, (Number(next[index].qty) || 0) + 1) };
      globalThis.localStorage?.setItem(CART_STORAGE_KEY, JSON.stringify(next));
    } else {
      globalThis.localStorage?.setItem(CART_STORAGE_KEY, JSON.stringify([...current, { packageKey: key, qty: 1 }]));
    }

    setCartCount(cartCountFromStorage());
  };

  return (
    <div className="min-h-dvh overflow-x-hidden bg-[#050508] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,70,229,0.14),transparent_45%),radial-gradient(circle_at_bottom_left,rgba(212,175,55,0.1),transparent_45%)]" />

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.25em]">Numeroloji.app</span>
          </Link>

          <div className="hidden items-center gap-8 text-xs font-semibold uppercase tracking-[0.18em] text-white/70 md:flex">
            <a href="#analizler" className="transition-colors hover:text-[#d4af37]">
              Numeroloji
            </a>
            <a href="#paketler" className="transition-colors hover:text-[#d4af37]">
              Analiz Paketleri
            </a>
            <a href="#cemberler" className="transition-colors hover:text-[#d4af37]">
              Çemberler
            </a>
            <Link to="/numeroloji/magaza" className="transition-colors hover:text-[#d4af37]">
              Mağaza
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/numeroloji/sepet" className="relative rounded-full border border-white/15 p-2" aria-label="Sepet">
              <ShoppingBag className="h-4 w-4" />
              {cartCount ? (
                <span
                  className="absolute -right-1 -top-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-black"
                  style={{ backgroundColor: GOLD }}
                >
                  {cartCount}
                </span>
              ) : null}
            </Link>
            <Link to="/panel" className="rounded-full border border-white/15 p-2" aria-label="Panel">
              <User className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative z-10">
        <section className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 py-20 text-center">
          <h1 className="text-5xl font-bold leading-tight md:text-7xl">
            Enerjini Keşfet
            <br />
            <span className="bg-gradient-to-r from-[#d4af37] to-[#f4e1a1] bg-clip-text text-transparent">Hayatını Dönüştür</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-white/70 md:text-lg">
            Ad-soyad ve doğum tarihi ile anında kısa numeroloji sonuçlarını gör, karmik borçlarını öğren.
          </p>
        </section>

        <section id="analizler" className="mx-auto max-w-7xl px-4 py-8">
          <div className="mb-7 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#d4af37]">İlahi Sayılar Rehberi</p>
            <h2 className="mt-3 text-3xl font-bold md:text-4xl">Kişisel Analiz Alanın</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-white/65 md:text-base">
              Bilgilerini gir, anında sonuç al. Bu alan hem hesaplama hem de kısa yorumları tek ekranda sunar.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))] p-6 shadow-[0_0_0_1px_rgba(212,175,55,0.1)] backdrop-blur">
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/20 px-3 py-1 text-xs uppercase tracking-[0.14em] text-white/80">
                  <Sparkles className="h-3.5 w-3.5 text-[#d4af37]" />
                  Karmik Borç
                </div>
                <span className="rounded-full border border-[#d4af37]/35 bg-[#d4af37]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#e7cc78]">
                  Hızlı Sonuç
                </span>
              </div>

              <h3 className="mt-4 text-2xl font-bold">Karmik Borç Hesaplama</h3>
              <p className="mt-2 text-sm text-white/65">İsim ve soyisminden karmik borç kodlarını anında hesaplar.</p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="k-first" className="text-white/80">Ad</Label>
                  <Input
                    id="k-first"
                    value={karmicForm.firstName}
                    onChange={(e) => setKarmicForm((p) => ({ ...p, firstName: e.target.value }))}
                    className="mt-2 h-11 border-white/20 bg-black/35 text-white placeholder:text-white/30"
                  />
                </div>
                <div>
                  <Label htmlFor="k-last" className="text-white/80">Soyad</Label>
                  <Input
                    id="k-last"
                    value={karmicForm.lastName}
                    onChange={(e) => setKarmicForm((p) => ({ ...p, lastName: e.target.value }))}
                    className="mt-2 h-11 border-white/20 bg-black/35 text-white placeholder:text-white/30"
                  />
                </div>
              </div>

              <Button onClick={handleKarmicDebt} className="mt-4 h-11 bg-[#d4af37] text-black hover:bg-[#c8a135]">
                Karmik Borcumu Hesapla
              </Button>

              {karmicCodes ? (
                <div className="mt-5 rounded-2xl border border-[#d4af37]/25 bg-black/35 p-4 text-sm">
                  <div className="font-semibold text-[#d4af37]">Sonuç</div>
                  {karmicCodes.length ? (
                    <ul className="mt-2 space-y-1.5 text-white/80">
                      {karmicCodes.map((code) => (
                        <li key={code}>{shortKarmicInfo(code)}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-2 text-white/75">Bu isim kombinasyonunda klasik karmik borç kodu görünmüyor.</div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[linear-gradient(165deg,rgba(20,20,35,0.9),rgba(11,11,18,0.9))] p-6 backdrop-blur">
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full border border-[#d4af37]/20" />
              <div className="pointer-events-none absolute -right-4 -top-4 h-28 w-28 rounded-full border border-[#d4af37]/25" />

              <div className="relative flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/25 px-3 py-1 text-xs uppercase tracking-[0.14em] text-white/80">
                  <Calculator className="h-3.5 w-3.5 text-[#d4af37]" />
                  Kısa Analiz
                </div>
                <span className="rounded-full border border-white/15 bg-black/25 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/65">
                  Tablo + Yorum
                </span>
              </div>

              <h3 className="relative mt-4 text-2xl font-bold">Kısa Numeroloji Analizi</h3>
              <p className="relative mt-2 text-sm text-white/65">Ad, soyad ve doğum tarihiyle temel tabloyu anında üretir.</p>

              <div className="relative mt-5 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="a-first" className="text-white/80">Ad</Label>
                  <Input
                    id="a-first"
                    value={analysisForm.firstName}
                    onChange={(e) => setAnalysisForm((p) => ({ ...p, firstName: e.target.value }))}
                    className="mt-2 h-11 border-white/20 bg-black/35 text-white placeholder:text-white/30"
                  />
                </div>
                <div>
                  <Label htmlFor="a-last" className="text-white/80">Soyad</Label>
                  <Input
                    id="a-last"
                    value={analysisForm.lastName}
                    onChange={(e) => setAnalysisForm((p) => ({ ...p, lastName: e.target.value }))}
                    className="mt-2 h-11 border-white/20 bg-black/35 text-white placeholder:text-white/30"
                  />
                </div>
                <div>
                  <Label htmlFor="a-day" className="text-white/80">Gün</Label>
                  <Input
                    id="a-day"
                    value={analysisForm.day}
                    onChange={(e) => setAnalysisForm((p) => ({ ...p, day: e.target.value }))}
                    className="mt-2 h-11 border-white/20 bg-black/35 text-white placeholder:text-white/30"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="a-month" className="text-white/80">Ay</Label>
                    <Input
                      id="a-month"
                      value={analysisForm.month}
                      onChange={(e) => setAnalysisForm((p) => ({ ...p, month: e.target.value }))}
                      className="mt-2 h-11 border-white/20 bg-black/35 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <Label htmlFor="a-year" className="text-white/80">Yıl</Label>
                    <Input
                      id="a-year"
                      value={analysisForm.year}
                      onChange={(e) => setAnalysisForm((p) => ({ ...p, year: e.target.value }))}
                      className="mt-2 h-11 border-white/20 bg-black/35 text-white placeholder:text-white/30"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleQuickAnalysis} className="relative mt-4 h-11 bg-[#d4af37] text-black hover:bg-[#c8a135]">
                Analizi Oluştur
              </Button>

              {analysisResult ? (
                <div className="relative mt-5 rounded-2xl border border-[#d4af37]/25 bg-black/35 p-4">
                  <Table>
                    <TableBody>
                      <TableRow><TableCell>Yaşam Yolu</TableCell><TableCell>{analysisResult.destinyNumber}</TableCell></TableRow>
                      <TableRow><TableCell>İfade</TableCell><TableCell>{analysisResult.expressionNumber}</TableCell></TableRow>
                      <TableRow><TableCell>Kalp Arzusu</TableCell><TableCell>{analysisResult.heartDesireNumber}</TableCell></TableRow>
                      <TableRow><TableCell>Kişilik</TableCell><TableCell>{analysisResult.personalityNumber}</TableCell></TableRow>
                      <TableRow><TableCell>Doğum Günü Sayısı</TableCell><TableCell>{analysisResult.birthDayNumber}</TableCell></TableRow>
                      <TableRow><TableCell>Pin Kodu</TableCell><TableCell>{analysisResult.pinText}</TableCell></TableRow>
                      <TableRow><TableCell>Karmik Borçlar</TableCell><TableCell>{analysisResult.karmicCodes.length ? analysisResult.karmicCodes.join(", ") : "Yok"}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                  <p className="mt-3 text-sm text-white/75">
                    Kısa yorum: Yaşam yolu {analysisResult.destinyNumber} ile ilerliyorsun. En güçlü çalışma alanın pin kodundaki {analysisResult.pinText} dizilimi.
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section id="paketler" className="mx-auto max-w-7xl px-4 py-16">
          <div className="mb-8 flex items-end justify-between">
            <h3 className="text-3xl font-bold">Numeroloji Analizleri</h3>
            <Link to="/numeroloji/magaza" className="text-sm uppercase tracking-[0.15em] text-[#d4af37]">
              Detaylar
            </Link>
          </div>
          {packages.length ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {packages.map((pkg) => {
                const inCart = cartItems.some((entry) => String(entry.packageKey ?? "") === String(pkg.key ?? ""));
                return (
                  <div
                    key={pkg.id}
                    className="group overflow-hidden rounded-xl border"
                    style={{
                      background: "linear-gradient(145deg, #151518, #0a0a0c)",
                      borderColor: "rgba(212,175,55,0.12)",
                    }}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[linear-gradient(135deg,#151518,#1b1b2c_45%,#2a2135)]">
                      {pkg.imageUrl?.trim() ? <img src={pkg.imageUrl} alt={pkg.name || "Paket görseli"} className="absolute inset-0 h-full w-full object-cover" loading="lazy" /> : null}
                      <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 [background:linear-gradient(to_top,rgba(10,10,12,0.88),transparent)]" />
                    </div>

                    <div className="p-5 text-center">
                      <div className="font-display text-2xl tracking-wide text-slate-100">{pkg.name}</div>
                      <p className="mt-2 line-clamp-2 text-xs text-slate-300/80">{pkg.description}</p>

                      <div className="mt-4 flex items-end justify-center gap-2">
                        {typeof pkg.discountedPriceTry === "number" && pkg.discountedPriceTry > 0 && pkg.discountedPriceTry < pkg.basePriceTry ? (
                          <span className="text-xs opacity-50 line-through">{formatTry(pkg.basePriceTry)}</span>
                        ) : null}
                        <p className="font-serif text-2xl italic tracking-wider" style={{ color: GOLD }}>
                          {formatTry(getPackageEffectivePrice(pkg))}
                        </p>
                      </div>

                      {inCart ? (
                        <Link
                          to="/numeroloji/sepet"
                          className="mt-5 inline-flex w-full items-center justify-center rounded border border-[rgba(212,175,55,0.40)] bg-[rgba(212,175,55,1)] py-3 text-xs font-display tracking-[0.22em] text-black shadow-[0_0_15px_rgba(212,175,55,0.30)] transition-all duration-300"
                        >
                          SEPETİ GÖRÜNTÜLE
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => addPackageToCart(pkg.key)}
                          className="mt-5 w-full rounded border border-[rgba(212,175,55,0.40)] bg-transparent py-3 text-xs font-display tracking-[0.22em] text-slate-200 transition-all duration-300 hover:bg-[rgba(212,175,55,1)] hover:text-black"
                        >
                          SEPETE EKLE
                        </button>
                      )}

                      <div className="mt-3">
                        <Link to="/numeroloji/magaza" className="text-[10px] uppercase tracking-[0.22em] opacity-60 hover:opacity-100">
                          Detayları İncele →
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-sm text-white/70">Henüz aktif analiz paketi bulunmuyor.</div>
          )}
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16">
          <div className="mb-8 flex items-end justify-between">
            <h3 className="text-3xl font-bold">Öne Çıkan Koleksiyon</h3>
            <Link to="/numeroloji/magaza" className="text-sm uppercase tracking-[0.15em] text-[#d4af37]">
              Tümünü Gör
            </Link>
          </div>
          {products.length ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {products.map((product) => (
                <div key={product.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="mb-4 aspect-[4/5] rounded-xl bg-[linear-gradient(135deg,#10162a,#2b1b3d)]" />
                  <div className="text-lg font-bold">{product.name}</div>
                  <div className="mt-1 line-clamp-2 text-sm text-white/65">{product.description}</div>
                  <div className="mt-4 font-semibold text-[#d4af37]">{formatTry(product.priceTry)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-sm text-white/70">Henüz gösterilecek ürün bulunmuyor.</div>
          )}
        </section>

        <section id="cemberler" className="mx-auto max-w-7xl px-4 py-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h3 className="text-center text-3xl font-bold md:text-5xl">Spiritüel Çemberler</h3>
            <Link to="/panel/cember" className="text-sm uppercase tracking-[0.15em] text-[#d4af37]">
              Tüm Çemberler
            </Link>
          </div>
          {circlesQuery.data?.length ? (
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {circlesQuery.data.slice(0, 3).map((circle) => (
                <Link key={circle.id} to="/panel/cember" className="flex flex-col items-center text-center">
                  <div className="relative h-56 w-56 rounded-full border-[12px] border-[#b9b6ac] bg-[#c8c5bb] p-[8px] shadow-[0_12px_35px_rgba(0,0,0,0.35)]">
                    <div className="h-full w-full overflow-hidden rounded-full border border-white/25 bg-[#8d9485]/70">
                      {circle.imageUrl ? (
                        <img src={circle.imageUrl} alt={circle.name || "Çember görseli"} className="h-full w-full object-cover" loading="lazy" />
                      ) : (
                        <div className="h-full w-full bg-[#8d9485]/70" />
                      )}
                    </div>
                  </div>

                  <div className="mt-5 max-w-[17rem] text-3xl font-display tracking-tight text-white md:text-4xl">{circle.name}</div>
                  <div className="mt-1 text-xl text-white/65 md:text-2xl">{circle.membersCount}+ Katılımcı</div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-sm text-white/70">Henüz listelenecek çember yok.</div>
          )}
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16">
          <h3 className="text-3xl font-bold">Aydınlanmış Ruhlar</h3>
          {reviews.length ? (
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {rotatingReviews.map((review) => (
                <div key={`${review.id}-${reviewStartIndex}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{review.name}</div>
                    <div className="text-[#d4af37]">{"★".repeat(Math.max(1, Math.min(5, review.rating || 5)))}</div>
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.12em] text-white/45">{review.packageKey}</div>
                  <p className="mt-3 text-sm text-white/75 line-clamp-4">{review.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-sm text-white/70">Henüz kullanıcı yorumu yok.</div>
          )}
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/10 bg-black/30 py-14">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-4">
          <div>
            <div className="font-display text-lg font-bold tracking-[0.25em]">Numeroloji.app</div>
            <p className="mt-3 text-sm text-white/55">Kadim bilgeliği modern teknolojiyle buluşturan numeroloji deneyimi.</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#d4af37]">Keşfet</div>
            <div className="mt-3 space-y-2 text-sm text-white/65">
              <a href="#analizler" className="block">
                Numeroloji
              </a>
              <a href="#paketler" className="block">
                Analiz Paketleri
              </a>
              <a href="#cemberler" className="block">
                Topluluk
              </a>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#d4af37]">Mağaza</div>
            <div className="mt-3 space-y-2 text-sm text-white/65">
              <Link to="/numeroloji/magaza" className="block">
                Ürünler
              </Link>
              <Link to="/numeroloji/magaza" className="block">
                Paketler
              </Link>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#d4af37]">Yönetim</div>
            <div className="mt-3 text-sm text-white/65">
              <Link to="/admin/numeroloji" className="underline underline-offset-4">
                Yönetici girişi
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-10 text-center text-xs text-white/45">© {new Date().getFullYear()} Numeroloji.app. Tüm hakları saklıdır.</div>
      </footer>
    </div>
  );
}
